// Process Master — Feb 2026 restructure (v3, polished layout).
//
// Cleaner layout: the LIST is the primary view; adding/editing opens a
// modal so the page stays uncluttered. Behaviour is auto-derived from
// input/output units; incoming_store and outsource are protected.

import { useState, useEffect, useCallback } from "react";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { toast } from "sonner";
import axios from "axios";
import Layout from "../components/Layout";
import MasterExcelToolbar from "../components/MasterExcelToolbar";
import { Plus, PencilSimple, Trash, ArrowUp, ArrowDown, Check, Lock, Factory } from "@phosphor-icons/react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const UNITS = ["KG", "Litres", "Nos", "Set"];
const PROCESS_TYPES = [
  { code: "raw_material", label: "Raw Material" },
  { code: "wip", label: "WIP" },
  { code: "semi_finish", label: "Semi Finish" },
  { code: "finished_good", label: "Finished Good" },
];
const PROTECTED_CODES = new Set(["incoming_store", "outsource"]);
const SYSTEM_BEHAVIOURS = new Set([
  "incoming_store", "outsource", "firewall", "semi_firewall",
  "dispatch", "assembly",
]);

const CUTTING_TYPES = [
  { code: "laser",     label: "Laser Cutting" },
  { code: "bandsaw",   label: "Bandsaw Cutting" },
  { code: "shearing",  label: "Shearing" },
  { code: "water_jet", label: "Water Jet" },
];
// Business categories that power the Cost Sheet badge.
const PROCESS_CATEGORIES = [
  { code: "fabrication",       label: "Fabrication" },
  { code: "surface_treatment", label: "Surface Treatment" },
  { code: "surface_finish",    label: "Surface Finish" },
];
// Default slab rates for a bandsaw process (₹ per sq. inch) — editable
// per tenant. Prefilled when the user first switches to Bandsaw.
const BANDSAW_DEFAULT_SLABS = {
  bandsaw_rate_upto_100: 1,
  bandsaw_rate_100_200: 1.5,
  bandsaw_rate_above_200: 2,
};

function deriveBehaviour(inp, out, existing) {
  if (existing && SYSTEM_BEHAVIOURS.has(existing)) return existing;
  if ((inp || "").toUpperCase() === "KG" && (out || "").toLowerCase() === "nos") return "cutting";
  return "normal";
}

const blankForm = () => ({
  name: "",
  requires_password: true, has_remarks: false,
  is_inhouse: true, is_outsource: false,
  input_unit: "Nos", output_unit: "Nos",
  process_type: "",
  process_category: "fabrication",
  standardised_rate: "",
  standardised_unit: "",
  _existingBehaviour: "normal",
  // Cutting-specific — only relevant when behaviour resolves to "cutting".
  cutting_type: "",
  cutting_rate_per_mm_thickness: "",
  cutting_rate_per_kg: "",
  bandsaw_rate_upto_100: "",
  bandsaw_rate_100_200: "",
  bandsaw_rate_above_200: "",
});

export default function ProcessMaster() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(blankForm());
  const [editingId, setEditingId] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [rateUnits, setRateUnits] = useState([]);  // dropdown source for Unit
  const [categories, setCategories] = useState([]); // dropdown source for Process Category

  const loadUnits = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/rate-units`);
      setRateUnits(r.data || []);
    } catch { /* silent — the field just shows manual entry */ }
  }, []);

  const loadCategories = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/process-categories`);
      setCategories(r.data || []);
    } catch { /* silent — the field just shows manual entry */ }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const pr = await axios.get(`${API}/processes`);
      setRows((pr.data || []).slice().sort((a, b) => (a.sequence || 0) - (b.sequence || 0)));
    } catch { toast.error("Load failed"); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); loadUnits(); loadCategories(); }, [load, loadUnits, loadCategories]);

  // Quick-add for a new unit — POSTs to /api/rate-units and refreshes.
  const addRateUnit = async (name) => {
    const clean = String(name || "").trim();
    if (!clean) return;
    try {
      await axios.post(`${API}/rate-units`, { name: clean });
      await loadUnits();
      toast.success(`Unit "${clean}" added`);
      setForm(f => ({ ...f, standardised_unit: clean }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not add unit");
    }
  };

  // Quick-add for a new category — POSTs to /api/process-categories.
  const addCategory = async (name) => {
    const clean = String(name || "").trim();
    if (!clean) return;
    try {
      const r = await axios.post(`${API}/process-categories`, { name: clean });
      await loadCategories();
      const code = r.data?.code || clean.toLowerCase().replace(/\s+/g, "_");
      toast.success(`Category "${clean}" added`);
      setForm(f => ({ ...f, process_category: code }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not add category");
    }
  };

  const notifyChange = () => window.dispatchEvent(new Event("processes:changed"));

  const openAdd = () => { setEditingId(null); setForm(blankForm()); setDialogOpen(true); };
  const openEdit = (r) => {
    setEditingId(r.id);
    setForm({
      name: r.name || "",
      requires_password: !!r.requires_password, has_remarks: !!r.has_remarks,
      // Backward-compat default: rows saved before Feb 27 2026 don't have
      // the flags — treat them as in-house only.
      is_inhouse:   r.is_inhouse   === undefined ? true  : !!r.is_inhouse,
      is_outsource: r.is_outsource === undefined ? false : !!r.is_outsource,
      input_unit: r.input_unit || "Nos", output_unit: r.output_unit || "Nos",
      process_type: r.process_type || "",
      process_category: r.process_category || "fabrication",
      standardised_rate: r.standardised_rate ?? "",
      standardised_unit: r.standardised_unit || "",
      _existingBehaviour: r.behaviour || "normal",
      cutting_type: r.cutting_type || "",
      cutting_rate_per_mm_thickness: r.cutting_rate_per_mm_thickness ?? "",
      cutting_rate_per_kg: r.cutting_rate_per_kg ?? "",
      bandsaw_rate_upto_100: r.bandsaw_rate_upto_100 ?? "",
      bandsaw_rate_100_200: r.bandsaw_rate_100_200 ?? "",
      bandsaw_rate_above_200: r.bandsaw_rate_above_200 ?? "",
    });
    setDialogOpen(true);
  };

  const buildPayload = (f, includeSequence = false) => {
    const beh = deriveBehaviour(f.input_unit, f.output_unit, f._existingBehaviour);
    const asNum = (v) => (v === "" || v == null ? null : parseFloat(v));
    const doc = {
      // `code` and `prefix` are auto-generated on the backend from the name
      // so business owners don't have to think about them.
      name: f.name.trim(),
      behaviour: beh,
      requires_password: !!f.requires_password,
      has_remarks: !!f.has_remarks,
      is_inhouse:   !!f.is_inhouse,
      is_outsource: !!f.is_outsource,
      input_unit: f.input_unit || null,
      output_unit: f.output_unit || null,
      process_type: f.process_type || null,
      process_category: f.process_category || "fabrication",
      standardised_rate: asNum(f.standardised_rate),
      standardised_unit: f.standardised_unit || null,
    };
    // Only stamp cutting fields when the process is actually a cutting one —
    // keeps other rows lean.
    if (beh === "cutting") {
      doc.cutting_type = f.cutting_type || null;
      doc.cutting_rate_per_mm_thickness = asNum(f.cutting_rate_per_mm_thickness);
      doc.cutting_rate_per_kg = asNum(f.cutting_rate_per_kg);
      doc.bandsaw_rate_upto_100 = asNum(f.bandsaw_rate_upto_100);
      doc.bandsaw_rate_100_200 = asNum(f.bandsaw_rate_100_200);
      doc.bandsaw_rate_above_200 = asNum(f.bandsaw_rate_above_200);
    }
    if (includeSequence) doc.sequence = rows.length + 1;
    return doc;
  };

  const save = async () => {
    if (!form.name.trim()) return toast.error("Process name is required");
    try {
      if (editingId) {
        await axios.put(`${API}/processes/${editingId}`, buildPayload(form));
        toast.success("Process updated");
      } else {
        await axios.post(`${API}/processes`, buildPayload(form, true));
        toast.success("Process added");
      }
      setDialogOpen(false); setEditingId(null); setForm(blankForm());
      load(); notifyChange();
    } catch (e) { toast.error("Save failed: " + (e.response?.data?.detail || e.message)); }
  };

  const remove = async (row) => {
    if (PROTECTED_CODES.has(row.code)) {
      toast.error(`'${row.name}' is a core system process and cannot be deleted.`);
      return;
    }
    if (!window.confirm(`Delete process '${row.name}'?\n\nThis also removes it from every part's operations, opening balances, rates and op-passwords. Cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/processes/${row.id}`);
      toast.success(`'${row.name}' deleted`); load(); notifyChange();
    } catch (e) { toast.error("Delete failed: " + (e.response?.data?.detail || e.message)); }
  };

  const move = async (idx, dir) => {
    const newOrder = [...rows];
    const target = idx + dir;
    if (target < 0 || target >= newOrder.length) return;
    [newOrder[idx], newOrder[target]] = [newOrder[target], newOrder[idx]];
    setRows(newOrder);
    try { await axios.post(`${API}/processes/reorder`, newOrder.map(r => r.id)); notifyChange(); }
    catch { toast.error("Reorder failed"); load(); }
  };

  const previewFormUI = () => {
    const b = deriveBehaviour(form.input_unit, form.output_unit, form._existingBehaviour);
    if (b === "cutting") return "Cutting-style form (Kg → Nos conversion)";
    if (SYSTEM_BEHAVIOURS.has(b)) return `Specialised '${b.replace("_", " ")}' form (system-defined, preserved)`;
    return "Standard operation form";
  };

  return (
    <Layout title="Process Master">
      <div className="p-6 space-y-5 max-w-[1400px] mx-auto">
        {/* ─── Page header ─── */}
        <PageHeader
          icon={Factory}
          title="Process Master"
          subtitle="Manufacturing operations available on the shop floor. Order here decides the sidebar sequence."
          rightSlot={
            <div className="flex items-center gap-2 flex-wrap">
              <MasterExcelToolbar basePath="/process-master" entityLabel="Processes"
                onImportDone={() => { load(); notifyChange(); }} size="sm" />
              <Button className="bg-[#1D2951] hover:bg-[#2A3B73] text-white gap-1 h-9" onClick={openAdd} data-testid="pm-add-btn">
                <Plus size={14} /> Add Process
              </Button>
            </div>
          }
        />

        {/* ─── Table ─── */}
        <Card className="border border-[#E2E8F0] overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#F8FAFC] text-[#475569] text-[11px] uppercase tracking-wider">
                <tr>
                  <th className="px-3 py-3 text-center w-20 font-semibold">Order</th>
                  <th className="px-4 py-3 text-left font-semibold">Name</th>
                  <th className="px-3 py-3 text-center font-semibold">Input</th>
                  <th className="px-3 py-3 text-center font-semibold">Output</th>
                  <th className="px-3 py-3 text-left font-semibold">Type</th>
                  <th className="px-3 py-3 text-center font-semibold w-28">Location</th>
                  <th className="px-3 py-3 text-center font-semibold w-24">Password</th>
                  <th className="px-3 py-3 text-center font-semibold w-28">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#F1F5F9]">
                {loading && <tr><td colSpan={8} className="py-10 text-center text-[#64748B]">Loading…</td></tr>}
                {!loading && rows.length === 0 && <tr><td colSpan={8} className="py-12 text-center text-[#94A3B8]">No processes yet — click <b>Add Process</b> to create your first one.</td></tr>}
                {rows.map((r, idx) => {
                  const isProtected = PROTECTED_CODES.has(r.code);
                  return (
                    <tr key={r.id} className="hover:bg-[#F8FAFC] transition-colors" data-testid={`pm-row-${r.code}`}>
                      <td className="px-3 py-3 text-center">
                        <div className="inline-flex items-center gap-1 text-[#64748B]">
                          <span className="text-[11px] font-mono w-4 text-right">{idx + 1}</span>
                          <button onClick={() => move(idx, -1)} className="p-0.5 rounded hover:bg-[#E2E8F0]" title="Move up"><ArrowUp size={12} /></button>
                          <button onClick={() => move(idx, 1)} className="p-0.5 rounded hover:bg-[#E2E8F0]" title="Move down"><ArrowDown size={12} /></button>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <span className="font-semibold text-[#0F172A]">{r.name}</span>
                          {isProtected && <Lock size={12} className="text-[#DC2626]" weight="fill" title="Core system process — protected" />}
                        </div>
                      </td>
                      <td className="px-3 py-3 text-center text-[#334155]">{r.input_unit || "—"}</td>
                      <td className="px-3 py-3 text-center text-[#334155]">{r.output_unit || "—"}</td>
                      <td className="px-3 py-3 text-[#475569]">{PROCESS_TYPES.find(t => t.code === r.process_type)?.label || "—"}</td>
                      <td className="px-3 py-3 text-center">
                        <div className="inline-flex gap-1 flex-wrap justify-center">
                          {(r.is_inhouse === undefined || r.is_inhouse) && (
                            <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-[#DCFCE7] text-[#166534]">In-house</span>
                          )}
                          {r.is_outsource && (
                            <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-[#FEF3C7] text-[#B45309]">Outsource</span>
                          )}
                        </div>
                      </td>
                      <td className="px-3 py-3 text-center">{r.requires_password ? "🔒" : "—"}</td>
                      <td className="px-3 py-3 text-center whitespace-nowrap">
                        <Button size="sm" variant="ghost" onClick={() => openEdit(r)} title="Edit"><PencilSimple size={14} /></Button>
                        <Button size="sm" variant="ghost" onClick={() => remove(r)}
                          className={isProtected ? "text-[#94A3B8] cursor-not-allowed" : "text-[#DC2626]"}
                          title={isProtected ? "Core system process — cannot be deleted" : "Delete"}>
                          <Trash size={14} />
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>

        <p className="text-[11px] text-[#94A3B8]">
          <Lock size={11} className="inline mr-1 text-[#DC2626]" weight="fill" />
          Marked rows are core system processes and cannot be deleted — they power the RM and Outsource flows.
          The transaction form for each process is chosen automatically from its Input / Output units.
        </p>
      </div>

      {/* ─── Add / Edit modal ─── */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{editingId ? "Edit Process" : "Add Process"}</DialogTitle>
          </DialogHeader>

          <div className="space-y-4 pt-2">
            <FormSection title="Identity">
              <FieldGrid cols={2}>
                <Field label="Process Name *">
                  <Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })}
                    placeholder="e.g. CNC Turning" data-testid="pm-name" />
                </Field>
              </FieldGrid>
            </FormSection>

            <FormSection title="Units & Type"
              hint={`Form: ${previewFormUI()}`}>
              <FieldGrid cols={3}>
                <Field label="Input Unit">
                  <SelectPlain value={form.input_unit} onChange={v => setForm({ ...form, input_unit: v })}
                    options={UNITS.map(u => ({ code: u, label: u }))} testId="pm-input-unit" />
                </Field>
                <Field label="Output Unit">
                  <SelectPlain value={form.output_unit} onChange={v => setForm({ ...form, output_unit: v })}
                    options={UNITS.map(u => ({ code: u, label: u }))} testId="pm-output-unit" />
                </Field>
                <Field label="Process Type">
                  <SelectPlain value={form.process_type} onChange={v => setForm({ ...form, process_type: v })}
                    options={[{ code: "", label: "— None —" }, ...PROCESS_TYPES]} testId="pm-process-type" />
                </Field>
              </FieldGrid>
              <div className="mt-3">
                <FieldGrid cols={3}>
                  <Field label="Process Category"
                    hint="Shown as a badge on the Cost Sheet.">
                    <UnitPicker value={form.process_category}
                      units={categories.map(c => ({ id: c.id, name: c.name, code: c.code }))}
                      valueBy="code"
                      onChange={v => setForm({ ...form, process_category: v })}
                      onAddUnit={addCategory}
                      testIdSelect="pm-process-category"
                      testIdAdd="pm-add-category-btn"
                      testIdInput="pm-new-category-input"
                      testIdSave="pm-new-category-save"
                      placeholder="— Select category —" />
                  </Field>
                  <Field label="Standardised Rate (₹)">
                    <Input type="number" step="0.01" value={form.standardised_rate}
                      onChange={e => setForm({ ...form, standardised_rate: e.target.value })}
                      placeholder="e.g. 12.50"
                      data-testid="pm-standardised-rate" />
                  </Field>
                  <Field label="Unit"
                    hint="Pick a saved unit or click + to add a new one.">
                    <UnitPicker value={form.standardised_unit}
                      units={rateUnits}
                      onChange={v => setForm({ ...form, standardised_unit: v })}
                      onAddUnit={addRateUnit} />
                  </Field>
                </FieldGrid>
              </div>
            </FormSection>

            {/* Flags are meaningless for cutting-style processes (which have
                their own dedicated Kg→Nos form), so we hide the section for
                those. Every other behaviour still gets password / remarks
                toggles. */}
            <FormSection title="Location · In-house / Outsource"
              hint="Tick both if the process may run either way. Reports show only In-house columns.">
              <FieldGrid cols={4}>
                <CheckboxField id="pm-inh" label="In-house" checked={form.is_inhouse}
                  onChange={v => setForm({ ...form, is_inhouse: v })} testId="pm-is-inhouse" />
                <CheckboxField id="pm-out" label="Outsource" checked={form.is_outsource}
                  onChange={v => setForm({ ...form, is_outsource: v })} testId="pm-is-outsource" />
              </FieldGrid>
            </FormSection>

            {deriveBehaviour(form.input_unit, form.output_unit, form._existingBehaviour) !== "cutting" && (
              <FormSection title="Flags">
                <FieldGrid cols={4}>
                  <CheckboxField id="pm-pw" label="Password gate" checked={form.requires_password}
                    onChange={v => setForm({ ...form, requires_password: v })} testId="pm-requires-password" />
                  <CheckboxField id="pm-rem" label="Remarks field" checked={form.has_remarks}
                    onChange={v => setForm({ ...form, has_remarks: v })} testId="pm-has-remarks" />
                </FieldGrid>
              </FormSection>
            )}

            {/* Cutting-specific: type-of-cutting picker + rate matrix. */}
            {deriveBehaviour(form.input_unit, form.output_unit, form._existingBehaviour) === "cutting" && (
              <FormSection title="Cutting Setup"
                hint="These rates power the Cutting section of the Cost Sheet.">
                <FieldGrid cols={3}>
                  <Field label="Type of Cutting">
                    <SelectPlain value={form.cutting_type}
                      onChange={v => {
                        setForm((prev) => {
                          const next = { ...prev, cutting_type: v };
                          // Prefill bandsaw slab defaults the first time a
                          // user picks "bandsaw" — but only when all three
                          // are still empty (never overwrite existing input).
                          if (v === "bandsaw"
                            && !prev.bandsaw_rate_upto_100
                            && !prev.bandsaw_rate_100_200
                            && !prev.bandsaw_rate_above_200) {
                            next.bandsaw_rate_upto_100 = BANDSAW_DEFAULT_SLABS.bandsaw_rate_upto_100;
                            next.bandsaw_rate_100_200 = BANDSAW_DEFAULT_SLABS.bandsaw_rate_100_200;
                            next.bandsaw_rate_above_200 = BANDSAW_DEFAULT_SLABS.bandsaw_rate_above_200;
                          }
                          return next;
                        });
                      }}
                      options={[{ code: "", label: "— Select cutting type —" }, ...CUTTING_TYPES]}
                      testId="pm-cutting-type" />
                  </Field>
                </FieldGrid>

                {(form.cutting_type === "laser" || form.cutting_type === "water_jet") && (
                  <div className="mt-3">
                    <FieldGrid cols={3}>
                      <Field label="Rate per mm per Thickness (₹)"
                        hint={form.cutting_type === "laser" ? "Laser" : "Water Jet"}>
                        <Input type="number" step="0.0001"
                          value={form.cutting_rate_per_mm_thickness}
                          onChange={e => setForm({ ...form, cutting_rate_per_mm_thickness: e.target.value })}
                          placeholder="e.g. 0.0075"
                          data-testid="pm-cutting-rate-mm" />
                      </Field>
                    </FieldGrid>
                  </div>
                )}

                {form.cutting_type === "shearing" && (
                  <div className="mt-3">
                    <FieldGrid cols={3}>
                      <Field label="Rate per Kg (₹)">
                        <Input type="number" step="0.01"
                          value={form.cutting_rate_per_kg}
                          onChange={e => setForm({ ...form, cutting_rate_per_kg: e.target.value })}
                          placeholder="e.g. 5"
                          data-testid="pm-cutting-rate-kg" />
                      </Field>
                    </FieldGrid>
                  </div>
                )}

                {form.cutting_type === "bandsaw" && (
                  <div className="mt-3 space-y-1">
                    <p className="text-[10px] text-[#94A3B8]">Slab rates in ₹ / sq. inch — auto-picked by the Cost Sheet based on the part&apos;s diameter.</p>
                    <FieldGrid cols={3}>
                      <Field label="Up to 100 mm Dia (₹/sq.in.)">
                        <Input type="number" step="0.01"
                          value={form.bandsaw_rate_upto_100}
                          onChange={e => setForm({ ...form, bandsaw_rate_upto_100: e.target.value })}
                          data-testid="pm-bandsaw-slab-100" />
                      </Field>
                      <Field label="100 – 200 mm Dia (₹/sq.in.)">
                        <Input type="number" step="0.01"
                          value={form.bandsaw_rate_100_200}
                          onChange={e => setForm({ ...form, bandsaw_rate_100_200: e.target.value })}
                          data-testid="pm-bandsaw-slab-200" />
                      </Field>
                      <Field label="Above 200 mm Dia (₹/sq.in.)">
                        <Input type="number" step="0.01"
                          value={form.bandsaw_rate_above_200}
                          onChange={e => setForm({ ...form, bandsaw_rate_above_200: e.target.value })}
                          data-testid="pm-bandsaw-slab-above" />
                      </Field>
                    </FieldGrid>
                  </div>
                )}
              </FormSection>
            )}
          </div>

          <div className="flex justify-end gap-2 pt-2 border-t border-[#E2E8F0] mt-2">
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button className="bg-[#1D2951] text-white" onClick={save} data-testid="pm-save">
              {editingId ? <><Check size={14} /> Save Changes</> : <><Plus size={14} /> Add Process</>}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}

/* ── Shared layout primitives (kept local so masters can share the vibe). ── */

function PageHeader({ icon: Icon, title, subtitle, rightSlot }) {
  return (
    <div className="flex items-start justify-between gap-4 flex-wrap">
      <div className="flex items-start gap-3">
        {Icon && <div className="p-2 rounded-lg bg-[#1D2951]/5 text-[#1D2951]"><Icon size={20} weight="fill" /></div>}
        <div>
          <h1 className="text-2xl font-black tracking-tight text-[#0F172A]" style={{ fontFamily: 'Chivo' }}>{title}</h1>
          {subtitle && <p className="text-xs text-[#64748B] mt-0.5 max-w-2xl">{subtitle}</p>}
        </div>
      </div>
      {rightSlot}
    </div>
  );
}

function FormSection({ title, hint, children }) {
  return (
    <div>
      <div className="flex items-baseline justify-between mb-2">
        <h3 className="text-[10px] font-bold uppercase tracking-widest text-[#64748B]">{title}</h3>
        {hint && <span className="text-[10px] text-[#94A3B8] italic">{hint}</span>}
      </div>
      {children}
    </div>
  );
}

function FieldGrid({ cols = 4, children }) {
  const grid = { 2: "grid-cols-2", 3: "grid-cols-3", 4: "grid-cols-4" }[cols] || "grid-cols-4";
  return <div className={`grid ${grid} gap-3`}>{children}</div>;
}

function Field({ label, hint, span = 1, children }) {
  const spanCls = span === 2 ? "col-span-2" : span === 3 ? "col-span-3" : "";
  return (
    <div className={spanCls}>
      <Label className="text-[11px] font-semibold text-[#334155] flex items-center gap-1">
        {label}{hint && <span className="text-[10px] font-normal text-[#94A3B8]">({hint})</span>}
      </Label>
      <div className="mt-0.5">{children}</div>
    </div>
  );
}

function CheckboxField({ id, label, checked, onChange, testId }) {
  return (
    <label htmlFor={id} className="flex items-center gap-2 h-full pt-5 cursor-pointer">
      <input id={id} type="checkbox" className="w-4 h-4" checked={checked}
        onChange={e => onChange(e.target.checked)} data-testid={testId} />
      <span className="text-xs font-semibold text-[#334155]">{label}</span>
    </label>
  );
}

function SelectPlain({ value, onChange, options, testId }) {
  return (
    <select value={value} onChange={e => onChange(e.target.value)}
      className="w-full min-h-[38px] border border-[#E2E8F0] rounded px-2 text-xs bg-white"
      data-testid={testId}>
      {options.map(o => <option key={o.code} value={o.code}>{o.label}</option>)}
    </select>
  );
}


// Small dropdown+button combo for the "Unit" field on Process Master.
// Reused for the Process Category dropdown too — the `valueBy` prop chooses
// whether the option's value is its `.name` (default, for units) or `.code`
// (for categories where the persisted value is a stable slug).
function UnitPicker({ value, units, onChange, onAddUnit, valueBy = "name", placeholder = "— Select unit —",
                     testIdSelect = "pm-standardised-unit", testIdAdd = "pm-add-unit-btn",
                     testIdInput = "pm-new-unit-input", testIdSave = "pm-new-unit-save" }) {
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState("");
  const submit = () => {
    if (!draft.trim()) return;
    onAddUnit(draft.trim());
    setDraft("");
    setAdding(false);
  };
  return (
    <div className="flex items-center gap-1">
      <select value={value || ""} onChange={e => onChange(e.target.value)}
        className="flex-1 min-h-[38px] border border-[#E2E8F0] rounded px-2 text-xs bg-white"
        data-testid={testIdSelect}>
        <option value="">{placeholder}</option>
        {(units || []).map(u => {
          const v = valueBy === "code" ? (u.code || u.name) : u.name;
          return <option key={u.id || u.code || u.name} value={v}>{u.name}</option>;
        })}
      </select>
      {!adding ? (
        <Button type="button" variant="outline" size="sm"
          onClick={() => setAdding(true)}
          className="h-9 w-9 p-0 shrink-0"
          title="Add a new option"
          data-testid={testIdAdd}>
          <Plus size={14} />
        </Button>
      ) : (
        <div className="flex items-center gap-1">
          <Input value={draft}
            autoFocus placeholder="New value"
            onChange={e => setDraft(e.target.value)}
            onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); submit(); }
              else if (e.key === "Escape") { setAdding(false); setDraft(""); } }}
            className="h-9 w-28 text-xs" data-testid={testIdInput} />
          <Button type="button" size="sm" onClick={submit}
            className="h-9 px-2 bg-[#0F766E] hover:bg-[#0D5F58] text-white"
            data-testid={testIdSave}>
            <Check size={14} />
          </Button>
        </div>
      )}
    </div>
  );
}
